// gatsby-browser.js
import "./src/styles/main.scss";

/** TRACKING CONFIG */
const PH_TOKEN =
  process.env.GATSBY_POSTHOG_KEY || "phc_zNRLRhVsZeHVmbLHhP9Gj0GZYvblujLGumWLMUn2IEM";
const PH_HOST = process.env.GATSBY_POSTHOG_HOST || "https://eu.i.posthog.com";
const GTM_ID = "GTM-N4XP5FTN";
const CLARITY_ID = "mxghtn71ec";
const CONSENT_COOKIE = "gatsby-plugin-gdpr-cookies-v2";
const LEGACY_CONSENT_COOKIE = "gatsby-plugin-gdpr-cookies";

const getCookieValue = (name) => {
  if (typeof document === "undefined") return null;

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));

  return cookie ? cookie.slice(name.length + 1) : null;
};

const hasConsent = () => {
  if (typeof document === "undefined") return false;

  return getCookieValue(CONSENT_COOKIE) === "true";
};

const migrateLegacyConsent = () => {
  const currentConsent = getCookieValue(CONSENT_COOKIE);
  const legacyConsent = getCookieValue(LEGACY_CONSENT_COOKIE);

  if (currentConsent === null && legacyConsent === "true") {
    document.cookie = `${CONSENT_COOKIE}=true; Path=/; Max-Age=31536000; SameSite=Lax`;
  }

  if (legacyConsent !== null) {
    document.cookie = `${LEGACY_CONSENT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
  }
};

const trackingAllowedHere = () => {
  if (typeof window === "undefined") return false;

  const enableDevTracking = process.env.GATSBY_ENABLE_TRACKING_IN_DEV === "true";
  return enableDevTracking || window.location.hostname !== "localhost";
};

const loadScript = (src) => {
  if (document.querySelector(`script[src="${src}"]`)) return;

  const script = document.createElement("script");
  script.async = true;
  script.crossOrigin = "anonymous";
  script.src = src;
  document.head.appendChild(script);
};

const googleConsent = (status, command = "update") => {
  if (typeof window === "undefined") return;

  const consent = {
    analytics_storage: status,
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
  };

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
  window.gtag("consent", command, {
    ...consent,
    ...(command === "default" ? { wait_for_update: 500 } : {}),
  });

  if (command === "update") {
    window.dataLayer.push({ event: "consent_update", ...consent });
  }
};

const clarityConsent = (status) => {
  if (typeof window === "undefined" || typeof window.clarity !== "function") return;

  window.clarity("consentv2", {
    ad_Storage: status,
    analytics_Storage: status,
  });
};

const initClarity = () => {
  if (!CLARITY_ID) return;

  if (typeof window.clarity !== "function") {
    window.clarity = function clarity() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  }

  clarityConsent("granted");
  loadScript(`https://www.clarity.ms/tag/${CLARITY_ID}`);
};

const initGtm = () => {
  if (!GTM_ID) return;

  const src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  if (document.querySelector(`script[src="${src}"]`)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  loadScript(src);
};

let posthogClient = null;
let posthogInitPromise = null;

const initPostHog = () => {
  if (posthogClient) return Promise.resolve(posthogClient);
  if (posthogInitPromise) return posthogInitPromise;

  posthogInitPromise = import("posthog-js")
    .then((posthogModule) => {
      const posthog =
        posthogModule.posthog || posthogModule.default?.posthog || posthogModule.default;

      posthog.init(PH_TOKEN, {
        api_host: PH_HOST,
        person_profiles: "identified_only",
        capture_pageview: false,
      });
      posthog.opt_in_capturing();

      posthogClient = posthog;
      window.posthog = posthog;
      return posthog;
    })
    .catch((error) => {
      posthogInitPromise = null;
      console.error("PostHog failed to initialize", error);
      return null;
    });

  return posthogInitPromise;
};

let trackersStarted = false;

const initTrackers = () => {
  if (!trackingAllowedHere() || !hasConsent()) return Promise.resolve(null);

  if (!trackersStarted) {
    trackersStarted = true;
    initClarity();
    initGtm();
  }

  return initPostHog();
};

let lastTrackedUrl = null;

const capturePageView = async (location) => {
  if (!trackingAllowedHere() || !hasConsent()) return;

  const posthog = await initTrackers();
  if (!hasConsent()) return;

  const currentUrl = window.location.href;
  if (lastTrackedUrl === currentUrl) return;
  lastTrackedUrl = currentUrl;

  posthog?.capture?.("$pageview", { $current_url: currentUrl });
  window.dataLayer?.push({
    event: "pageview",
    page_location: currentUrl,
    page_path: location?.pathname || window.location.pathname,
    page_title: document.title,
  });
};

export const onClientEntry = () => {
  document.documentElement.classList.remove("no-js");
  migrateLegacyConsent();
  googleConsent("denied", "default");
};

export const onInitialClientRender = () => {
  if (hasConsent()) {
    googleConsent("granted");
    capturePageView(window.location);
  }

  window.addEventListener("cookieConsentAccepted", () => {
    googleConsent("granted");
    capturePageView(window.location);
  });

  window.addEventListener("cookieConsentDeclined", () => {
    googleConsent("denied");
    clarityConsent("denied");
    posthogClient?.opt_out_capturing?.();
    posthogClient?.reset?.(true);
    lastTrackedUrl = null;
  });
};

export const onRouteUpdate = ({ location }) => {
  capturePageView(location);
};
